(function () {
  const currencyCodePattern = /((?:€\s*)?\d[\d\s.,]*)(?:\s*)EUR\b/g;
  const subscriptionSelector = [
    '.shopify_subscriptions_app_block',
    '[class*="shopify-subscriptions"]',
    '[class*="shopify_subscriptions"]',
    '[class*="subscription"]',
    '[id*="subscription"]',
  ].join(',');
  const productSelector = 'product-info, .product, .product__info-container';
  const subscriptionChoiceSelector = [
    'input[name="selling_plan"]',
    'select[name="selling_plan"]',
    'input[name*="purchase"]',
    'select[name*="purchase"]',
    'input[name*="subscription"]',
    'select[name*="subscription"]',
  ].join(',');
  const oneTimeLabelPatterns = [
    /one[\s-]?time/i,
    /one[\s-]?off/i,
    /single purchase/i,
    /unique purchase/i,
    /compra [úu]nica/i,
    /compra uma vez/i,
    /comprar uma vez/i,
    /pagamento [úu]nico/i,
    /sem subscri/i,
  ];
  const purchaseChoiceState = new WeakMap();
  let isSettingDefaultPurchaseChoice = false;

  const normalizeValue = (value = '') => value.replace(/\s+/g, ' ').trim().toLowerCase();

  const escapeCssIdentifier = (value = '') => (window.CSS?.escape ? CSS.escape(value) : value.replace(/"/g, '\\"'));

  const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const parseLabelOverrides = (rawValue = '') =>
    rawValue
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [source, ...targetParts] = line.split('|');
        const target = targetParts.join('|').trim();

        return {
          source: source ? source.trim() : '',
          sourceNormalized: normalizeValue(source),
          target,
        };
      })
      .filter(({ source, target }) => source && target)
      .sort((first, second) => second.source.length - first.source.length);

  const getLabelOverrides = (element) => {
    const productInfo = element.closest('[data-subscription-label-overrides]');

    if (!productInfo) {
      return [];
    }

    const rawValue = productInfo.getAttribute('data-subscription-label-overrides') || '';

    if (productInfo.__subscriptionLabelOverridesRaw !== rawValue) {
      productInfo.__subscriptionLabelOverridesRaw = rawValue;
      productInfo.__subscriptionLabelOverrides = parseLabelOverrides(rawValue);
    }

    return productInfo.__subscriptionLabelOverrides || [];
  };

  const applyLabelOverrides = (value, overrides) => {
    let nextValue = value;

    overrides.forEach(({ source, sourceNormalized, target }) => {
      if (!source || !target) {
        return;
      }

      if (normalizeValue(nextValue) === sourceNormalized) {
        nextValue = target;
        return;
      }

      if (!normalizeValue(nextValue).includes(sourceNormalized)) {
        return;
      }

      nextValue = nextValue.replace(new RegExp(escapeRegExp(source), 'gi'), target);
    });

    return nextValue;
  };

  const cleanTextNode = (node, overrides = []) => {
    const nextValue = applyLabelOverrides(node.nodeValue.replace(currencyCodePattern, '$1'), overrides);

    if (nextValue !== node.nodeValue) {
      node.nodeValue = nextValue;
    }
  };

  const hasSubscriptionWidget = (element) => {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }

    return element.matches(subscriptionSelector) || Boolean(element.querySelector(subscriptionSelector));
  };

  const getCleanupContainer = (element) => {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    if (element.matches(subscriptionSelector)) {
      return element.closest(productSelector) || element;
    }

    if (hasSubscriptionWidget(element)) {
      return element.matches(productSelector) ? element : element.closest(productSelector) || element;
    }

    const subscriptionContainer = element.closest(subscriptionSelector);

    if (subscriptionContainer) {
      return subscriptionContainer.closest(productSelector) || subscriptionContainer;
    }

    const productContainer = element.closest(productSelector);

    return hasSubscriptionWidget(productContainer) ? productContainer : null;
  };

  const cleanSubscriptionContainer = (container) => {
    const overrides = getLabelOverrides(container);
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      cleanTextNode(walker.currentNode, overrides);
    }
  };

  const getProductContainer = (element) => element?.closest?.('product-info') || element?.closest?.(productSelector);

  const getControlLabelText = (control) => {
    const textContainers = [];
    const explicitLabel = control.id && document.querySelector(`label[for="${escapeCssIdentifier(control.id)}"]`);
    const wrappingLabel = control.closest('label');
    const optionContainer = control.closest(
      'label, [role="radio"], [class*="option"], [class*="Option"], [class*="selling"], [class*="Selling"], [class*="purchase"], [class*="Purchase"], [class*="subscription"], [class*="Subscription"]'
    );

    [explicitLabel, wrappingLabel, optionContainer].forEach((element) => {
      if (element && !textContainers.includes(element)) {
        textContainers.push(element);
      }
    });

    return textContainers.map((element) => element.textContent || '').join(' ');
  };

  const isOneTimeValue = (value = '') => {
    const normalizedValue = normalizeValue(value);

    return ['', 'none', 'null', 'false', 'one-time', 'one_time', 'onetime', 'one time'].includes(normalizedValue);
  };

  const hasOneTimeLabel = (control) => oneTimeLabelPatterns.some((pattern) => pattern.test(getControlLabelText(control)));

  const findOneTimeControl = (container) => {
    const controls = Array.from(container.querySelectorAll(subscriptionChoiceSelector));
    const select = controls.find((control) => {
      if (control.tagName !== 'SELECT') {
        return false;
      }

      return Array.from(control.options).some((option) => isOneTimeValue(option.value) || oneTimeLabelPatterns.some((pattern) => pattern.test(option.textContent || '')));
    });

    if (select) {
      return select;
    }

    return controls.find((control) => {
      if (control.type === 'hidden') {
        return control.name === 'selling_plan' && isOneTimeValue(control.value);
      }

      return isOneTimeValue(control.value) || hasOneTimeLabel(control);
    });
  };

  const dispatchFormChange = (control) => {
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const selectOneTimeControl = (control) => {
    if (control.tagName === 'SELECT') {
      const oneTimeOption = Array.from(control.options).find(
        (option) => isOneTimeValue(option.value) || oneTimeLabelPatterns.some((pattern) => pattern.test(option.textContent || ''))
      );

      if (oneTimeOption && control.value !== oneTimeOption.value) {
        control.value = oneTimeOption.value;
        dispatchFormChange(control);
        return true;
      }

      return false;
    }

    if (control.name === 'selling_plan' && control.type === 'hidden') {
      if (control.value !== '') {
        control.value = '';
        dispatchFormChange(control);
        return true;
      }

      return false;
    }

    if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) {
      control.checked = true;
      dispatchFormChange(control);
      return true;
    }

    if (control.name === 'selling_plan' && control.value !== '') {
      control.value = '';
      dispatchFormChange(control);
      return true;
    }

    return false;
  };

  const defaultToOneTimePurchase = (container) => {
    const productContainer = getProductContainer(container);

    if (!productContainer || productContainer.dataset.requiresSellingPlan === 'true') {
      return;
    }

    const state = purchaseChoiceState.get(productContainer);

    if (state?.userInteracted) {
      return;
    }

    const oneTimeControl = findOneTimeControl(productContainer);

    if (!oneTimeControl) {
      return;
    }

    isSettingDefaultPurchaseChoice = true;
    let changed = false;

    try {
      changed = selectOneTimeControl(oneTimeControl);
    } finally {
      isSettingDefaultPurchaseChoice = false;
    }

    if (changed) {
      purchaseChoiceState.set(productContainer, {
        userInteracted: false,
      });
    }
  };

  const syncSubscriptionContainer = (container) => {
    cleanSubscriptionContainer(container);
    defaultToOneTimePurchase(container);
  };

  const cleanSubscriptionPrices = (root = document) => {
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) {
      return;
    }

    if (root.nodeType === Node.ELEMENT_NODE) {
      const container = getCleanupContainer(root);

      if (container) {
        syncSubscriptionContainer(container);
        return;
      }
    }

    root.querySelectorAll(subscriptionSelector).forEach((container) => {
      syncSubscriptionContainer(container.closest(productSelector) || container);
    });
  };

  document.addEventListener(
    'change',
    (event) => {
      if (isSettingDefaultPurchaseChoice || !event.target?.matches?.(subscriptionChoiceSelector)) {
        return;
      }

      const productContainer = getProductContainer(event.target);

      if (productContainer) {
        purchaseChoiceState.set(productContainer, {
          userInteracted: true,
        });
      }
    },
    true
  );

  const observeSubscriptionWidgets = () => {
    cleanSubscriptionPrices();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          const container = getCleanupContainer(mutation.target.parentElement);

          if (container) {
            cleanTextNode(mutation.target, getLabelOverrides(container));
          }

          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const container = getCleanupContainer(node.parentElement);

            if (container) {
              cleanTextNode(node, getLabelOverrides(container));
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const container = getCleanupContainer(node);

            if (container) {
              syncSubscriptionContainer(container);
            } else {
              cleanSubscriptionPrices(node);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeSubscriptionWidgets);
  } else {
    observeSubscriptionWidgets();
  }
})();
